import {AwsV4Signer} from 'aws4fetch';
import awsConfiguration from 'awsConfiguration';
import {getSessionSync} from './LFCCognito';

/**
 * S3 会社バケットの画像を Cognito Identity Pool の STS 一時クレデンシャルで
 * presigned URL 化するユーティリティ。
 * 会社スコープの強制は IAM 側（Cognito_LFCAuth_Role の ABAC ＋ バケットの
 * DenyOtherCompanyCognitoAccess）で行われる。ここでは署名を付けるだけ。
 */

const COGNITO_IDENTITY_ENDPOINT = `https://cognito-identity.${awsConfiguration.region}.amazonaws.com/`;
const USER_POOL_PROVIDER = `cognito-idp.${awsConfiguration.region}.amazonaws.com/${awsConfiguration.UserPoolId}`;
// STS 期限（1時間）の5分前に再取得
const CREDS_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// presign 対象外（公開のまま運用するバケット）
const PUBLIC_BUCKETS = ['lfc-commons', 'lfc-map-image'];

export interface AwsCredentials {
  accessKeyId: string;
  secretKey: string;
  sessionToken: string;
  expiration: number; // epoch ms
}

let cachedCreds: AwsCredentials | null = null;
let inflight: Promise<AwsCredentials | null> | null = null;
let cacheEpoch = 0;

const callCognitoIdentity = async (target: string, body: object) => {
  const res = await fetch(COGNITO_IDENTITY_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityService.${target}`
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    throw new Error(`cognito-identity ${target} failed: ${res.status}`);
  }
  return await res.json();
};

const fetchCredentials = async (): Promise<AwsCredentials | null> => {
  const session = await getSessionSync();
  if (session == null) {
    return null;
  }
  const logins = {[USER_POOL_PROVIDER]: session.getIdToken().getJwtToken()};
  const {IdentityId} = await callCognitoIdentity('GetId', {
    IdentityPoolId: awsConfiguration.IdentityPoolId,
    Logins: logins
  });
  const {Credentials} = await callCognitoIdentity('GetCredentialsForIdentity', {
    IdentityId,
    Logins: logins
  });
  return {
    accessKeyId: Credentials.AccessKeyId,
    secretKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiration: Credentials.Expiration * 1000
  };
};

/**
 * Identity Pool の STS 一時クレデンシャルを返す（メモリキャッシュ・同時要求は集約）。
 * 未ログイン・取得失敗時は null。
 */
export const getIdentityCredentials = async (): Promise<AwsCredentials | null> => {
  if (cachedCreds != null && cachedCreds.expiration - CREDS_REFRESH_BUFFER_MS > Date.now()) {
    return cachedCreds;
  }
  const epochAtRequest = cacheEpoch;
  if (inflight == null) {
    inflight = fetchCredentials()
      .catch(error => {
        console.error(`getIdentityCredentials failed: ${error}`);
        return null;
      })
      .finally(() => {
        inflight = null;
      });
  }
  const creds = await inflight;
  if (epochAtRequest !== cacheEpoch) {
    // ログアウト等でキャッシュが無効化された場合、旧世代の結果は使わない
    return null;
  }
  if (creds != null) {
    cachedCreds = creds;
    return creds;
  }
  // 取得失敗時: 期限内の旧クレデンシャルが残っていればそれで凌ぐ（期限切れは使わない）
  return cachedCreds != null && cachedCreds.expiration > Date.now() ? cachedCreds : null;
};

/** ログアウト時・テスト用のキャッシュクリア */
export const clearIdentityCredentialsCache = (): void => {
  cacheEpoch += 1;
  cachedCreds = null;
  inflight = null;
};

/**
 * virtual-hosted-style の S3 URL からバケット名とリージョンを取り出す。
 * S3 URL でなければ null。リージョン無しのレガシー形式は ap-northeast-1 とみなす。
 */
export const parseS3Url = (url: string): {bucket: string; region: string} | null => {
  let host: string;
  try {
    host = new URL(url).host;
  } catch {
    return null;
  }
  const withRegion = host.match(
    /^([a-z0-9][a-z0-9.-]*)\.s3[.-]([a-z]{2}-[a-z]+-\d)\.amazonaws\.com$/
  );
  if (withRegion != null) {
    return {bucket: withRegion[1], region: withRegion[2]};
  }
  const legacy = host.match(/^([a-z0-9][a-z0-9.-]*)\.s3\.amazonaws\.com$/);
  if (legacy != null) {
    return {bucket: legacy[1], region: 'ap-northeast-1'};
  }
  return null;
};

/**
 * S3 会社バケットの URL を presigned GET URL に変換する。
 * S3 以外の URL・公開バケット・未ログイン時は元の URL をそのまま返す（フォールバック）。
 * 既存のクエリ（cache-bust 等）は署名に含めて保持される。
 * 有効期限は X-Amz-Expires デフォルト（実効は STS クレデンシャル期限 ≦1時間）。
 */
export const presignS3Url = async (url: string): Promise<string> => {
  const parts = parseS3Url(url);
  if (parts == null || PUBLIC_BUCKETS.includes(parts.bucket)) {
    return url;
  }
  const creds = await getIdentityCredentials();
  if (creds == null) {
    return url;
  }
  try {
    const signer = new AwsV4Signer({
      method: 'GET',
      url,
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretKey,
      sessionToken: creds.sessionToken,
      service: 's3',
      region: parts.region,
      signQuery: true
    });
    const signed = await signer.sign();
    return signed.url.toString();
  } catch (error) {
    console.error(`presignS3Url failed: ${error}`);
    return url;
  }
};
